import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import { useAuthentication } from '@/contexts/Authentication';
import { Optional } from '@/types/generics';
import { GeneratedUseQueryHook } from '@/types/genericQueries';
import { useApolloClientAuthentication } from '@/components/AuthenticatedApolloProvider';

/**
 * __useAuthenticatedQuery__
 *
 * Run a GraphQL query once user authentication and JWT request tokens have been initialized.
 * Ensures that queries are not executed before authentication state is complete, with a user
 * running in the browser, not in server-side prebuild, and with a valid JWT token applied to
 * the Apollo client.
 *
 * Note, if the user is not authenticated or no token is initialized, this hook will simply
 * remain idle.
 *
 * The `loading` property of the query return will reflect the loading authentication state
 * as well as the loading state of the query itself.
 *
 * @param queryFunction the query function to run once authenticated
 * @param options options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAuthenticatedQuery(useStaffQuery, {
 *   variables: {
 *      staffId: // value for 'staffId'
 *   },
 * });
 */
const useAuthenticatedQuery = <
  TData,
  TVariables extends OperationVariables,
  AwaitedVariables extends string
>(
  queryFunction: GeneratedUseQueryHook<TData, TVariables>,
  options: QueryHookOptions<TData, Optional<TVariables, AwaitedVariables>> & {
    waitFor?: AwaitedVariables[];
  } = {}
): QueryResult<TData, TVariables> => {
  // There can be a sync issue between authentication data being provided by useAuthentication
  // and the Apollo client having been updated. Therefore ApolloClientConfigurator sets a ready
  // state in context.
  const { clientAuthenticated } = useApolloClientAuthentication();
  const {
    hasToken,
    authUser,
    authenticated,
    isReady: authenticationReady,
  } = useAuthentication();

  // If the user is not authenticated and auth cycle is complete:
  const resolvedLoggedOut = authenticationReady && !authenticated;

  // If a token is expired (pending refresh), we skip the query.
  const tokenExpired = !!(authUser && authUser.expires.getTime() < Date.now());

  // Authentication state is not available:
  const liminalAuth = !clientAuthenticated || !hasToken || tokenExpired;

  // Auth is considered _pending_ if liminal conditions are present, if user is not for-sure logged out:
  const pendingAuth = liminalAuth && !resolvedLoggedOut;

  // Resolve waitFor fields
  const waitingForVariables = options.waitFor
    ? options.waitFor.some(
        (field) =>
          options.variables?.[field] === undefined &&
          options.variables?.[field] !== null
      )
    : false;

  const fullOptions = {
    ...(options as unknown as QueryHookOptions<TData, TVariables>),
    // Never authenticated in SSR
    ssr: false,
    // Skip query until valid authentication token is in place
    skip:
      waitingForVariables || !!options.skip || liminalAuth || resolvedLoggedOut,
    // Skip polling attempts that occur while the token is expired
    skipPollAttempt: () => tokenExpired || options.skipPollAttempt?.() || false,
  };
  const functionState = queryFunction(fullOptions);
  return {
    ...functionState,
    // We extend the loading state to include 'pending auth', which is effectively
    // delaying `loading` of the underlying query until auth is resolved. This allows
    // consumers to wait against `loading` as they would for an unauthenticated query.
    loading: pendingAuth || functionState.loading,
  };
};

export default useAuthenticatedQuery;
